/* eslint-disable max-classes-per-file */
import { JsonPipe } from "@angular/common";
import {
    Component,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { Observable, Subject, of } from "rxjs";
import { delay } from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IRadiobuttonItem } from "../../components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { IOption } from "../../models/options-models";
import { FilterPipe } from "../../pipes/filter.pipe";
import { HighlightPipe } from "../../pipes/highlight.pipe";
import { RaskNewlinesPipe } from "../../pipes/rask-newlines.pipe";
import { StringFormatPipe } from "../../pipes/string-format.pipe";
import { StringLinkifyPipe } from "../../pipes/string-linkify.pipe";
import { FormBinderService } from "../../services/form-binder.service";
import { ActionButtonComponent } from "../action-button/action-button.component";
import { BusySpinnerComponent } from "../busy/busy-spinner/busy-spinner.component";
import { BusyTreeComponent } from "../busy/busy-tree/busy-tree.component";
import { ButtonComponent } from "../button/button.component";
import { CardComponent } from "../cards/card/card.component";
import { CardsComponent } from "../cards/cards.component";
import { CheckboxComponent } from "../checkbox/checkbox.component";
import { CollapsableComponent } from "../collapsable/collapsable.component";
import { BlockComponent } from "../container-block/block.component";
import { DatepickerIntervalComponent } from "../datepicker-interval/datepicker-interval.component";
import { DatepickerComponent } from "../datepicker/datepicker.component";
import { HeaderComponent } from "../header/header.component";
import { HelpiconComponent } from "../helpicon/helpicon.component";
import { HotDocComponent } from "../hot-doc/hot-doc.component";
import { Cellstyle, NumericCellChangeArg } from "../hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "../hot-doc/hotdoc/doc";
import { HotRow } from "../hot-doc/hotdoc/row";
import { IconStackedComponent } from "../icon-stacked/icon-stacked.component";
import { IconComponent } from "../icon/icon.component";
import { InfoComponent } from "../info/info.component";
import { InlineToastComponent } from "../inline-toast/inline-toast.component";
import { InputNumberComponent } from "../input-number/input-number.component";
import { InputComponent } from "../input/input.component";
import { LabelComponent } from "../label/label.component";
import { ListSelectedItemsComponent } from "../list-selected-items/list-selected-items.component";
import { SelectedListItem } from "../list-selected-items/listSelectedItems.models";
import { DivListComponent } from "../list/div-list/div-list.component";
import { ListColumnDirective } from "../list/list-column.directive";
import { ListComponent } from "../list/list.component";
import { IMapComponent } from "../map/map.models";
import { IMapSettings, IRenderParams } from "../map/map.settings";
import { MenuButtonComponent } from "../menu-button/menu-button.component";
import { MessagePanelComponent } from "../message-panel/message-panel.component";
import { IMessageLabel, IMessageModel, MessageSeverity } from "../message-panel/message-panel.models";
import { MultiRangeSelectComponent } from "../multi-range-select/multi-range-select.component";
import { MultiSelectorComponent } from "../multi-selector/multi-selector.component";
import { PageComponent } from "../page/page.component";
import { PillboxComponent } from "../pillbox/pillbox.component";
import { RadiobuttonGroupComponent } from "../radiobuttongroup/radiobuttongroup.component";
import { SaveButtonComponent } from "../save-button/save-button.component";
import { SelectorComponent } from "../selector/selector.component";
import { SimpleSelectorComponent } from "../simple-selector/simple-selector.component";
import { SliderComponent } from "../slider/slider.component";
import { SpanComponent } from "../span/span.component";
import { RaskColumn } from "../table/models/rask-column";
import { TableComponent } from "../table/table.component";
import { TabComponent } from "../tabs/tab/tab.component";
import { TabsComponent } from "../tabs/tabs.component";
import { TagComponent } from "../tag/tag.component";
import { TextExpanderComponent } from "../text-expander/text-expander.component";
import { TextareaComponent } from "../textarea/textarea.component";
import { TextmultilineComponent } from "../textmultiline/textmultiline.component";
import { ToastService } from "../toast/toast.service";
import { TogglePanelComponent } from "../toggle-panel/toggle-panel.component";
import { SelectionItem } from "../tree-selector/selection-item";
import { TreeSelectorComponent } from "../tree-selector/tree-selector.component";
import { VerticalCollapsableComponent } from "../vertical-collapsable/vertical-collapsable.component";
import { ShowroomRaskButtonComponent } from "./buttons/rask-button/showroom-rask-button.component";
import { ShowroomValidation } from "./showroom.validation";
import { ShowroomValidationComponent } from "./validation/showroom-validation.component";
@Component({
    selector: "rask-showroom",
    styleUrls: ["./showroom.component.scss"],
    templateUrl: "./showroom.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RouterLink,
        FormsModule,
        ReactiveFormsModule,
        HeaderComponent,
        PageComponent,
        InputComponent,
        ButtonComponent,
        BlockComponent,
        TogglePanelComponent,
        CheckboxComponent,
        ActionButtonComponent,
        CollapsableComponent,
        MessagePanelComponent,
        VerticalCollapsableComponent,
        CardsComponent,
        CardComponent,
        TabsComponent,
        TabComponent,
        IconComponent,
        IconStackedComponent,
        InfoComponent,
        HelpiconComponent,
        BusySpinnerComponent,
        BusyTreeComponent,
        PillboxComponent,
        InlineToastComponent,
        InputNumberComponent,
        TextareaComponent,
        TextmultilineComponent,
        RadiobuttonGroupComponent,
        DatepickerComponent,
        DatepickerIntervalComponent,
        SelectorComponent,
        MultiSelectorComponent,
        SimpleSelectorComponent,
        TreeSelectorComponent,
        MultiRangeSelectComponent,
        TagComponent,
        SliderComponent,
        ShowroomValidationComponent,
        ShowroomRaskButtonComponent,
        SaveButtonComponent,
        SpanComponent,
        LabelComponent,
        TextExpanderComponent,
        MenuButtonComponent,
        ListComponent,
        ListColumnDirective,
        ListSelectedItemsComponent,
        DivListComponent,
        TableComponent,
        HotDocComponent,
        JsonPipe,
        HighlightPipe,
        FilterPipe,
        StringFormatPipe,
        StringLinkifyPipe,
        RaskNewlinesPipe
    ]
})
export class ShowroomComponent implements OnInit {

    @ViewChild("tableInstance") tableInstance: TableComponent;
    @ViewChild("listInstance") listInstance: DivListComponent;
    @ViewChild("listWithCheckboxesInstance") listWithCheckboxesInstance: ListComponent;
    @ViewChild("listWithCheckboxesInstanceAndSelectPopover") listWithCheckboxesInstanceAndSelectPopover: ListComponent;

    validationContext = ShowroomValidation.init();
    checked = true;
    model: TestModel;
    form: UntypedFormGroup;
    stringArray = [
        "Textrad 1",
        "Textrad 2",
        "Textrad 3"
    ];

    groupValues = [
        {
            g: "1",
            v: "Värde 1g1"
        },
        {
            g: "1",
            v: "Värde 2g1"
        },
        {
            g: "2",
            v: "Värde 1g2"
        },
        {
            g: "2",
            v: "Värde 2g2"
        },
        {
            g: "g3",
            v: "Värde 1g3"
        }
    ];

    tableData = [
        {
            namn: undefined,
            value: 1,
            other: "annat 1"
        },
        {
            namn: "namn 2",
            value: 2,
            other: "annat 2"
        },
        {
            namn: "namn 3",
            value: 3,

            other: "Mätinformation saknas för GT Vaaras Q, VELJEKSET VAARA OY, 0120",
            tooltipText: "Mätinformation saknas för GT Vaaras Q, VELJEKSET VAARA OY, 0121"
        }
    ];

    raskTableData = [
        {
            namn: undefined,
            value: 1,
            other: "annat 1"
        },
        {
            namn: "namn 2",
            value: 2,
            other: "annat 2"
        },
        {
            namn: "namn 3",
            value: 3,
            other: {

                text: "Mätinformation saknas för GT Vaaras Q, VELJEKSET VAARA OY, 0122",
                tooltipText: "Mätinformation saknas för GT Vaaras Q, VELJEKSET VAARA OY, 0123"
            }
        }
    ];

    raskTableColumns = [
        {
            headerName: "Namn",
            field: "namn",
            filter: true,
            sortable: true,
            type: "span",
            editable: true,
            resizable: true,
            rowDrag: true
        },
        {
            headerName: "Värde",
            field: "value",
            type: "span",
            resizable: true
        },
        {
            headerName: "Övrigt",
            field: "other",
            type: "span",
            sortable: true,
            resizable: true
        }
    ] as RaskColumn[];

    tableColumns = [
        {
            headerName: "Namn",
            rule: this.validationContext.current.required,
            field: "namn",
            filter: true,
            sortable: true,
            type: "text",
            editable: true,
            resizable: true,
            headerParams: {
                iconClass: "fal fa-info-circle",
                popoverMessage: "En informationstext"
            }
        },
        {
            headerName: "Värde",
            field: "value",
            type: "selector",
            values: [
                1,
                2,
                3,
                6,
                7,
                8
            ],
            resizable: true
        },
        {
            headerName: "Övrigt",
            field: "other",
            type: "span",
            sortable: true,
            resizable: true,
            initialWidth: 250,
            minWidth: 100,
            maxWidth: 500
        }
    ] as any[] as RaskColumn[];

    columnsCloned = this.clone(this.tableColumns);

    multiSelectData = of([
        {
            namn: "namn 1",
            value: 1,
            groupName: "Grupp 1",
            other: "annat 1"
        },
        {
            namn: "namn 2",
            value: 2,
            groupName: "Grupp 2",
            other: "annat 2"
        },
        {
            namn: "namn 3",
            value: 3,
            groupName: "Grupp 1",
            other: "annat 3"
        }
    ]);

    treeSelectorValues = [
        {
            text: "Värde 1",
            value: "1"
        },
        {
            children: [
                {
                    text: "Värde 2 Barn 1",
                    value: "2-1"
                },
                {
                    text: "Värde 2 Barn 2",
                    value: "2-2"
                }
            ],
            text: "Värde 2",
            value: "2"
        }
    ] as SelectionItem[];

    treeSelectorValuesWithSorting = this.clone(this.treeSelectorValues);

    treeClone = this.clone(this.treeSelectorValues);

    treeSelectorWithHeaderValues = this.clone(this.treeSelectorValues);

    pipeSF = {
        pirateSong: "{1} gastar på en {2} mans {0}",
        trans15: 15,
        transDeath: "död"
    };

    linkifyText = "läs mer på http://www.di.se.";
    linkifyEmailText = "Skicka mail till info@nomail.se";

    pipeFilter = {
        filterName: "sveaskog",
        objects: [
            {
                obj1: {
                    prop1: "a@gmail.com",
                    prop2: "a@sveaskog.com"
                }
            },
            { obj2: "c@gmail.com" },
            {
                obj3: {
                    prop3: {
                        prop4: "b@sveaskog.com"
                    }
                }
            },
            { obj4: "c@sveaskog.com" }
        ]
    };

    radioButtonExamples = {
        example1: [
            {
                key: "1",
                value: "Ett"
            },
            {
                key: "2",
                value: "Två"
            },
            {
                key: "3",
                value: "Tre"
            }
        ] as IRadiobuttonItem[],
        example2: [
            {
                key: "1",
                value: "Ja"
            },
            {
                key: "0",
                value: "Nej"
            }
        ] as IRadiobuttonItem[],
        example3: [
            {
                key: "1",
                value: "Ja"
            },
            {
                key: "0",
                value: "Nej"
            }
        ] as IRadiobuttonItem[]
    };

    raskMapSettings1 = {
        height: "600px",
        center: [
            17.9531,
            59.3565
        ],
        zoom: 10,
        zoomButtons: true,
        header: { title: "Rask kartexempel" },
        onLayerReady: (map, layer) => this.mapexampleLayerReady(map, layer),
        drawLayers: [
            {
                id: "rita",
                title: "Rita",
                visible: true,
                color: [
                    255,
                    0,
                    0,
                    0.5
                ],
                selectable: true,
                readonly: false,
                geometryTypes: [
                    "point",
                    "polyline",
                    "polygon",
                    "rectangle",
                    "circle"
                ],
                geometries: []
            }
        ],
        baselayers: [
            {
                id: "metriabakgrund",
                visible: true
            }
        ],
        featurelayers: [
            {
                id: "AdministrativaGranser",
                visible: true
            }
        ]
    } as IMapSettings;
    refreshMap = new Subject<IRenderParams>();

    multiRangeSelectItems: any[] = getMockPeople();
    multiRangeSelectValue: any[] = [];

    textExpanderText = loremIpsumLongest;

    messageLabels: IMessageLabel[] = [
        {
            severity: MessageSeverity.Critical,
            labelText: "Överriden feltext (Kritiskt fel)"
        }
    ];
    messageItems: IMessageModel[] = getMockMessageItems();
    messageItemsAsObservable: Observable<IMessageModel[]> = of(this.messageItems);
    messageLabelsClone = this.clone(this.messageLabels);
    messageItemsClone = this.clone(this.messageItems);

    togglespinner = true;
    buttonOptions = [
        {
            key: "1",
            value: "Tarantella"
        },
        {
            key: "2",
            value: "Blåmes"
        },
        {
            key: "3",
            value: "Norsk skogskatt brun"
        },
        {
            key: "4",
            value: "Fladdermus"
        }
    ] as IOption[];
    buttonOptionSelected: IOption;

    inlineToast1$ = new Subject<void>();
    inlineToast10$ = new Subject<void>();

    protected hotDocExample: HotDoc;
    protected hotDocExampleData = [
        {
            namn: "Tall",
            antalStammar: 100,
            antalHektar: 4
        },
        {
            namn: "Gran",
            antalStammar: 250,
            antalHektar: 9
        },
        {
            namn: "Björk",
            antalStammar: 80,
            antalHektar: 3
        }
    ];

    private readonly fb = inject(FormBinderService);
    private readonly toast = inject(ToastService);

    treeSelectorSortExpression = (a, b) => (a.text > b.text ? -1 : 1);

    groupFormatter = (item) => item.v;
    groupBy = (item) => item.g;
    outputFormatter = (item) => item ? item.v : null;

    imgFormatter = (item) => ObjectUtils.hasValue(item) ? "fal fa-clock" : "";

    sliderValueFormatter = (item) => `${Math.round(item * 100)} %`;

    ngOnInit() {
        this.model = new TestModel();
        this.form = this.fb.CreateGroup(this.model);
        this.hotDocExample = this.generateHotDocExample();
    }

    // logging
    log(e) {
        // eslint-disable-next-line no-console
        console.log(`works: ${e}`);
        this.toast.add(e);
    }

    logPage = () => {
        // eslint-disable-next-line no-console
        console.log("Page action click!");
    };

    clone(obj: any) {
        return JSON.parse(JSON.stringify(obj));
    }

    getPeople(term: string = null): Observable<any[]> {
        let items = getMockPeople();
        if (term) {
            items = items.filter(x => x.name.toLocaleLowerCase().indexOf(term.toLocaleLowerCase()) > -1);
        }
        return of(items).pipe(delay(500));
    }

    mapexampleLayerReady(map: IMapComponent, layer) {
        // Här var det tomt
    }

    multiRangeSelectOrderBy(list: any[]): void {
        const lessOrZero = (b: any, a: any): number => b.index > a.index ? -1 : 0;
        list.sort((a, b) => a.index > b.index ? 1 : lessOrZero(b, a));
    }

    multiRangeSelectViewFormatter(item: any) {
        return `${item.index}: ${item.name}`;
    }

    onRowDragEnd($event): void {
        console.warn($event);
        console.warn(this.raskTableData);
    }

    getSortedData(): void {
        console.warn(this.listInstance.getSortedData());
    }

    buttonSelect(option: IOption): void {
        this.buttonOptionSelected = option;
        this.log(option.value);
    }

    getSelectedListItems() {
        const items = this.listWithCheckboxesInstanceAndSelectPopover?.getSelectedRows();
        return ObjectUtils.isNullOrUndefined(items) ? [] : items.map(x => new SelectedListItem(x.value, x.namn, true));
    }

    removeFromSelection(item: SelectedListItem) {
        const filter = (result: any) => result.value === item.id;
        this.listWithCheckboxesInstanceAndSelectPopover?.removeItemFromSelectionList(filter);
    }

    protected scrollToView(anchorId: string) {
        if (anchorId === "top") {
            window.scrollTo(0, 0);
        } else {
            const element = document.querySelector(`#${anchorId}`);
            element.scrollIntoView();
        }
    }

    private generateHotDocExample(): HotDoc {
        const hotDocSettings: IHotDocSettings = {

        };

        const colFactory = () => {
            const columns: IHotColumnDefinition[] = [];
            columns.push({
                header: "Trädslagsnamn",
                width: 220,
                readOnly: true,
                cellName: "namn"
            });
            columns.push({
                header: "Antal stammar",
                width: 220,
                cellName: "antalStammar",
                type: "numeric"
            });
            columns.push({
                header: "Antal hektar",
                width: 220,
                cellName: "antalHa",
                type: "numeric"

            });

            return columns;
        };

        const onHotCellNumericChange = (dataRow: typeof this.hotDocExampleData[number], numberArg: NumericCellChangeArg, property: string) => {
            dataRow[property] = numberArg.newValue;
            numberArg.complete();
        };

        const rowBuilder = (row: HotRow, dataRow: typeof this.hotDocExampleData[number]) => {
            row.addText("namn", dataRow.namn, Cellstyle.Readonly);
            row.addNumeric("antalStammar", dataRow.antalStammar, null, (numberArg) => onHotCellNumericChange(dataRow, numberArg, "antalStammar"));
            row.addNumeric("antalHa", dataRow.antalHektar, null, (numberArg) => onHotCellNumericChange(dataRow, numberArg, "antalHektar"));
        };

        const rowFactory = (doc: HotDoc) => {
            this.hotDocExampleData.forEach(tradslag => {
                doc.createRow(row => rowBuilder(row, tradslag));
            });
        };

        return new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    }
}

class TestModel {
    checked: boolean;
    text: string;
    slider: number;
    multiSelect: number[];
    select: number;
    constructor() {
        this.checked = true;
        this.text = "";
        this.slider = 0.47;
        this.multiSelect = [
            1,
            3
        ];
        this.select = 1;
    }
}

const getMockMessageItems = (): IMessageModel[] => [
    {
        severity: MessageSeverity.Critical,
        message: "Mätinformation saknas för GT Vaaras Q, VELJEKSET VAARA OY, 0125",
        anchor: "#some-anchor"
    },
    {
        severity: MessageSeverity.Critical,
        message: loremIpsumLong,
        anchor: ""
    },
    {
        severity: MessageSeverity.Error,
        message: loremIpsumLonger,
        anchor: ""
    },
    {
        severity: MessageSeverity.Error,
        message: loremIpsumLongest,
        anchor: ""
    },
    {
        severity: MessageSeverity.Warning,
        message: `Lorem ipsum dolor sit amet,
                                                        consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                                                        labore et dolore magna aliqua. one`,
        anchor: ""
    },
    {
        severity: MessageSeverity.Warning,
        message: `Lorem ipsum dolor sit amet,
                                                        consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                                                        labore et dolore magna aliqua. two`,
        anchor: ""
    },
    {
        severity: MessageSeverity.Update,
        message: `Lorem ipsum dolor sit amet,
                                                        consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                                                        abore et dolore magna aliqua. three`,
        anchor: ""
    },
    {
        severity: MessageSeverity.Info,
        message: loremIpsumLong,
        anchor: "#click-me-i-am-anhor"
    }
];

function getMockPeople() {
    // eslint-disable-next-line sonarjs/no-clear-text-protocols
    const picLink = "http://placehold.it/32x32";
    return [
        {
            id: "5a15b13c36e7a7f00cf0d7cb",
            index: 2,
            isActive: true,
            picture: picLink,
            age: 23,
            name: "Karyn Wright",
            gender: "female",
            company: "ZOLAR",
            email: "karynwright@zolar.com",
            phone: "+1 (851) 583-2547"
        },
        {
            id: "5a15b13c2340978ec3d2c0ea",
            index: 3,
            isActive: false,
            picture: picLink,
            age: 35,
            name: "Rochelle Estes",
            gender: "female",
            company: "EXTRAWEAR",
            email: "rochelleestes@extrawear.com",
            phone: "+1 (849) 408-2029"
        },
        {
            id: "5a15b13c663ea0af9ad0dae8",
            index: 4,
            isActive: false,
            picture: picLink,
            age: 25,
            name: "Mendoza Ruiz",
            gender: "male",
            company: "ZYTRAX",
            email: "mendozaruiz@zytrax.com",
            phone: "+1 (904) 536-2020"
        },
        {
            id: "5a15b13cc9eeb36511d65acf",
            index: 5,
            isActive: false,
            picture: picLink,
            age: 39,
            name: "Rosales Russell",
            gender: "male",
            company: "ELEMANTRA",
            email: "rosalesrussell@elemantra.com",
            phone: "+1 (868) 473-3073"
        },
        {
            id: "5a15b13c728cd3f43cc0fe8a",
            index: 6,
            isActive: true,
            picture: picLink,
            age: 32,
            name: "Marquez Nolan",
            gender: "male",
            company: "MIRACLIS",
            email: "marqueznolan@miraclis.com",
            phone: "+1 (853) 571-3921"
        },
        {
            id: "5a15b13ca51b0aaf8a99c05a",
            index: 7,
            isActive: false,
            picture: picLink,
            age: 28,
            name: "Franklin James",
            gender: "male",
            company: "CAXT",
            email: "franklinjames@caxt.com",
            phone: "+1 (868) 539-2984"
        },
        {
            id: "5a15b13cc3b9381ffcb1d6f7",
            index: 8,
            isActive: false,
            picture: picLink,
            age: 24,
            name: "Elsa Bradley",
            gender: "female",
            company: "MATRIXITY",
            email: "elsabradley@matrixity.com",
            phone: "+1 (994) 583-3850"
        },
        {
            id: "5a15b13ce58cb6ff62c65164",
            index: 9,
            isActive: true,
            picture: picLink,
            age: 40,
            name: "Pearson Thompson",
            gender: "male",
            company: "EZENT",
            email: "pearsonthompson@ezent.com",
            phone: "+1 (917) 537-2178"
        },
        {
            id: "5a15b13c90b95eb68010c86e",
            index: 10,
            isActive: true,
            picture: picLink,
            age: 32,
            name: "Ina Pugh",
            gender: "female",
            company: "MANTRIX",
            email: "inapugh@mantrix.com",
            phone: "+1 (917) 450-2372"
        },
        {
            id: "5a15b13c2b1746e12788711f",
            index: 11,
            isActive: true,
            picture: picLink,
            age: 25,
            name: "Nguyen Elliott",
            gender: "male",
            company: "PORTALINE",
            email: "nguyenelliott@portaline.com",
            phone: "+1 (905) 491-3377"
        },
        {
            id: "5a15b13c605403381eec5019",
            index: 12,
            isActive: true,
            picture: picLink,
            age: 31,
            name: "Mills Barnett",
            gender: "male",
            company: "FARMEX",
            email: "millsbarnett@farmex.com",
            phone: "+1 (882) 462-3986"
        },
        {
            id: "5a15b13c67e2e6d1a3cd6ca5",
            index: 13,
            isActive: true,
            picture: picLink,
            age: 36,
            name: "Margaret Reynolds",
            gender: "female",
            company: "ROOFORIA",
            email: "margaretreynolds@rooforia.com",
            phone: "+1 (935) 435-2345"
        },
        {
            id: "5a15b13c947c836d177aa85c",
            index: 14,
            isActive: false,
            picture: picLink,
            age: 29,
            name: "Yvette Navarro",
            gender: "female",
            company: "KINETICA",
            email: "yvettenavarro@kinetica.com",
            phone: "+1 (807) 485-3824"
        },
        {
            id: "5a15b13c5dbbe61245c1fb73",
            index: 15,
            isActive: false,
            picture: picLink,
            age: 20,
            name: "Elisa Guzman",
            gender: "female",
            company: "KAGE",
            email: "elisaguzman@kage.com",
            phone: "+1 (868) 594-2919"
        },
        {
            id: "5a15b13c38fd49fefea8db80",
            index: 16,
            isActive: false,
            picture: picLink,
            age: 33,
            name: "Jodie Bowman",
            gender: "female",
            company: "EMTRAC",
            email: "jodiebowman@emtrac.com",
            phone: "+1 (891) 565-2560"
        },
        {
            id: "5a15b13c9680913c470eb8fd",
            index: 17,
            isActive: false,
            picture: picLink,
            age: 24,
            name: "Diann Booker",
            gender: "female",
            company: "LYRIA",
            email: "diannbooker@lyria.com",
            phone: "+1 (830) 555-3209"
        }
    ];
}

const loremIpsumLong = `Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam,
    quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.`;

const loremIpsumLonger = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam in vestibulum tortor.
    Integer quis tempus ligula. Ut vulputate purus id lectus lacinia, id hendrerit ex pulvinar.
    Sed pulvinar elit sit amet dolor tincidunt lacinia eu eu nisl.
    Cras gravida, lacus sed ornare condimentum, elit eros tincidunt mi, sit amet eleifend leo mi quis urna.`;

const loremIpsumLongest = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin quam tellus, pharetra eu malesuada iaculis, aliquam quis purus.
    teger quis turpis feugiat, condimentum erat ut, fermentum tellus.
    Praesent viverra nulla in ex rutrum tempor. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.
    Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas.
    Sed rutrum nibh nec augue fermentum maximus. Proin odio lorem, maximus vel risus sodales, aliquam varius sem.
    Suspendisse in tortor a sapien tristique dapibus non et ante. Aliquam erat volutpat. Morbi vulputate lacinia feugiat.
    Vestibulum porttitor euismod facilisis. Fusce sollicitudin posuere eros, ut lobortis purus suscipit quis.
    Vivamus aliquam, orci sed faucibus condimentum, erat eros sagittis massa, nec hendrerit ligula eros malesuada velit.
    Sed pellentesque gravida augue, mollis egestas tortor placerat quis. Nunc mauris metus, ultrices nec laoreet vitae, elementum ac dolor. Sed ac mattis ex.`;
